import { formatDate, formatPhone, normalizePhone, type User } from "@elektr/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Search } from "lucide-react";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Badge, Card, EmptyState, PageHeader, Spinner } from "@/components/ui/card";
import { ConfirmDialog, Dialog } from "@/components/ui/dialog";
import { Field, Input } from "@/components/ui/form";
import { Pagination } from "@/components/ui/pagination";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useDebounced } from "@/lib/hooks";
import { useT } from "@/lib/i18n";

type Role = "customer" | "electrician";
const PAGE_SIZE = 20;

const COPY = {
  customer: { title: "nav.customers", add: "users.add_customer", empty: "users.empty_customers" },
  electrician: {
    title: "nav.electricians",
    add: "users.add_electrician",
    empty: "users.empty_electricians",
  },
} as const;

export function CustomersPage() {
  return <UsersPage role="customer" />;
}
export function ElectriciansPage() {
  return <UsersPage role="electrician" />;
}

function UsersPage({ role }: { role: Role }) {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const q = useDebounced(text);
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<"new" | User | null>(null);
  const [toDelete, setToDelete] = useState<User | null>(null);

  const key = ["users", role, { q, page }];
  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/users/", {
          params: {
            query: { role, q: q || undefined, page, page_size: PAGE_SIZE, ordering: "-created_at" },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["users", role] });
    void qc.invalidateQueries({ queryKey: ["count"] });
  };

  const toggle = useMutation({
    mutationFn: async (u: User) =>
      unwrap(
        await api.PATCH("/api/users/{id}/", {
          params: { path: { id: u.id } },
          body: { is_active: !u.is_active },
        }),
      ),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  const remove = useMutation({
    mutationFn: async (u: User) => {
      const res = await api.DELETE("/api/users/{id}/", { params: { path: { id: u.id } } });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => {
      setToDelete(null);
      refresh();
    },
    onError: () => toast.error(t("common.error")),
  });

  const rows = query.data?.results ?? [];
  return (
    <>
      <PageHeader
        title={t(COPY[role].title)}
        actions={
          <Button onClick={() => setDialog("new")}>
            <Plus className="size-4" /> {t(COPY[role].add)}
          </Button>
        }
      />
      <Card className="mb-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3.5 size-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={t("users.search")}
            aria-label={t("users.search")}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </Card>

      {query.isLoading && (
        <div className="p-10 text-center">
          <Spinner className="size-8" />
        </div>
      )}
      {query.data && rows.length === 0 && <EmptyState>{t(COPY[role].empty)}</EmptyState>}
      {rows.length > 0 && (
        <Card className="divide-y divide-border p-0">
          {rows.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold">
                    {u.full_name || formatPhone(u.phone)}
                  </span>
                  {!u.is_active && <Badge tone="danger">{t("users.blocked")}</Badge>}
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatPhone(u.phone)}
                  {u.email ? ` · ${u.email}` : ""} ·{" "}
                  {t("users.properties", { n: u.properties_count ?? 0 })} ·{" "}
                  {formatDate(u.created_at)}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setDialog(u)}>
                  <Pencil className="size-4" /> {t("common.edit")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate(u)}
                >
                  {u.is_active ? t("users.block") : t("users.unblock")}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setToDelete(u)}>
                  {t("common.delete")}
                </Button>
              </div>
            </div>
          ))}
        </Card>
      )}
      <Pagination
        page={page}
        count={query.data?.count ?? 0}
        pageSize={PAGE_SIZE}
        onPage={setPage}
      />

      <UserDialog
        role={role}
        user={dialog && dialog !== "new" ? dialog : null}
        open={dialog !== null}
        onOpenChange={(o) => !o && setDialog(null)}
        onSaved={refresh}
      />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        title={t("users.delete_title")}
        message={t("users.delete_message", {
          name: toDelete?.full_name || formatPhone(toDelete?.phone),
        })}
        confirmLabel={t("common.delete")}
        pending={remove.isPending}
        onConfirm={() => toDelete && remove.mutate(toDelete)}
      />
    </>
  );
}

/** Yangi foydalanuvchi yaratish yoki mavjudini tahrirlash: ism, telefon (login), email, parol (esdan chiqqanda yangisini berish). */
function UserDialog({
  role,
  user,
  open,
  onOpenChange,
  onSaved,
}: {
  role: Role;
  user: User | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  // key: dialog har ochilganda (boshqa foydalanuvchi uchun ham) toza holatdan boshlanadi
  return open ? (
    <UserForm
      key={user?.id ?? "new"}
      role={role}
      user={user}
      onOpenChange={onOpenChange}
      onSaved={onSaved}
    />
  ) : null;
}

function UserForm({
  role,
  user,
  onOpenChange,
  onSaved,
}: {
  role: Role;
  user: User | null;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [name, setName] = useState(user?.full_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const normalized = normalizePhone(phone);
      if (!normalized) throw new Error("phone");
      const body = {
        phone: normalized,
        full_name: name,
        email: email.trim(),
        ...(password ? { password } : {}),
      };
      if (user)
        return unwrap(
          await api.PATCH("/api/users/{id}/", { params: { path: { id: user.id } }, body }),
        );
      return unwrap(await api.POST("/api/users/", { body: { ...body, role } }));
    },
    onSuccess: () => {
      toast.success(t("common.saved"));
      onOpenChange(false);
      onSaved();
    },
    onError: (e) =>
      setError(
        e instanceof Error && e.message === "phone"
          ? t("auth.phone_invalid")
          : errorMessage(e, t("common.error")),
      ),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password && password.length < 8) return setError(t("users.password_short"));
    save.mutate();
  };

  return (
    <Dialog open onOpenChange={onOpenChange} title={user ? t("users.edit") : t(COPY[role].add)}>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label={t("auth.phone")} hint={t("users.login_hint")}>
          {(id) => (
            <Input
              id={id}
              type="tel"
              inputMode="tel"
              placeholder="90 123 45 67"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
        </Field>
        <Field label={t("users.full_name")}>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label={t("users.email")} hint={t("users.email_hint")}>
          {(id) => (
            <Input
              id={id}
              type="email"
              inputMode="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <Field
          label={user ? t("users.password_new") : t("users.password_optional")}
          hint={user ? t("users.password_reset_hint") : t("users.password_hint")}
        >
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
